import useSWR from 'swr';
import { toast } from 'sonner';
import { useState } from 'react';
import { useParams } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { AnexosInput } from 'src/components/anexos-input';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { anexarPlantao, baixarPlantaoDoc } from 'src/actions/anexos';

import { formatDateBr } from '../Nfse/types';

// ----------------------------------------------------------------------
// Plantão Fiscal → detalhe da solicitação + acompanhamento (mensagens).
// Backend: GET /portal-api/plantao-fiscal/:id.

interface Mensagem {
  id: number;
  mensagem: string | null;
  data_hora: string | null;
  lida: boolean | null;
  do_contribuinte: boolean;
}

interface Anexo {
  id: number;
  titulo: string | null;
  nome: string | null;
  data_envio: string | null;
}

interface SolicitacaoDetalhe {
  solicitacao: {
    id: number;
    protocolo: string | null;
    titulo: string | null;
    assunto: string | null;
    situacao: number | null;
    data_abertura: string | null;
    anotacoes: string | null;
  };
  mensagens: Mensagem[];
  anexos: Anexo[];
}

function CampoInfo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Grid>
  );
}

export default function PlantaoFiscalDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, mutate } = useSWR<SolicitacaoDetalhe>(
    id ? `/portal-api/plantao-fiscal/${id}` : null,
    fetcher
  );
  const s = data?.solicitacao;
  const mensagens = data?.mensagens ?? [];
  const anexos = data?.anexos ?? [];

  const [novaMsg, setNovaMsg] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [anexosNovos, setAnexosNovos] = useState<File[]>([]);
  const [enviandoAnexos, setEnviandoAnexos] = useState(false);

  const enviarAnexos = async () => {
    if (!anexosNovos.length) return;
    setEnviandoAnexos(true);
    try {
      const falhas = await anexarPlantao(id, anexosNovos);
      if (falhas > 0) toast.error(`${falhas} anexo(s) não puderam ser enviados.`);
      else toast.success('Anexo(s) enviado(s).');
      setAnexosNovos([]);
      await mutate();
    } finally {
      setEnviandoAnexos(false);
    }
  };

  const responder = async () => {
    if (!novaMsg.trim()) return;
    setEnviando(true);
    try {
      await axiosInstance.post(`/portal-api/plantao-fiscal/${id}/mensagem`, {
        mensagem: novaMsg.trim(),
      });
      setNovaMsg('');
      await mutate();
    } catch {
      toast.error('Falha ao enviar a mensagem.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={s ? `Solicitação ${s.protocolo ?? ''}` : 'Solicitação'}
        links={[
          { name: 'Plantão Fiscal', href: '/plantao-fiscal' },
          { name: 'Solicitações', href: '/plantao-fiscal' },
          { name: 'Detalhe' },
        ]}
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      {!s ? (
        <Card sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {isLoading ? 'Carregando...' : 'Solicitação não encontrada.'}
          </Typography>
        </Card>
      ) : (
        <Stack spacing={3}>
          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Dados da solicitação
            </Typography>
            <Grid container spacing={2.5}>
              <CampoInfo label="Protocolo" value={s.protocolo} />
              <CampoInfo label="Assunto" value={s.assunto} />
              <CampoInfo label="Título" value={s.titulo} />
              <CampoInfo label="Abertura" value={formatDateBr(s.data_abertura)} />
              <CampoInfo
                label="Situação"
                value={<Label color="default">Situação {s.situacao ?? '—'}</Label>}
              />
            </Grid>
            {s.anotacoes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Anotações
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {s.anotacoes}
                </Typography>
              </>
            )}
          </Card>

          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Anexos
            </Typography>
            {anexos.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Nenhum anexo nesta solicitação.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {anexos.map((a) => (
                  <Box
                    key={a.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Iconify icon="eva:attach-2-fill" width={16} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="body2" noWrap>
                        {a.nome ?? `documento-${a.id}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        {formatDateBr(a.data_envio)}
                      </Typography>
                    </Box>
                    <Tooltip title="Baixar">
                      <IconButton size="small" onClick={() => baixarPlantaoDoc(id, a.id, a.nome ?? `documento-${a.id}`)}>
                        <Iconify icon="solar:download-bold" width={18} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            )}
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <AnexosInput arquivos={anexosNovos} onChange={setAnexosNovos} disabled={enviandoAnexos} />
              {anexosNovos.length > 0 && (
                <Button variant="contained" onClick={enviarAnexos} disabled={enviandoAnexos}>
                  Enviar {anexosNovos.length} anexo(s)
                </Button>
              )}
            </Stack>
          </Card>

          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Acompanhamento
            </Typography>
            {mensagens.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sem mensagens nesta solicitação.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {mensagens.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      display: 'flex',
                      justifyContent: m.do_contribuinte ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        maxWidth: '80%',
                        bgcolor: m.do_contribuinte ? 'primary.lighter' : 'background.neutral',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        {m.do_contribuinte ? 'Você' : 'Fiscal'} · {formatDateBr(m.data_hora)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {m.mensagem}
                      </Typography>
                    </Card>
                  </Box>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <TextField
                label="Escreva uma mensagem"
                value={novaMsg}
                onChange={(e) => setNovaMsg(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={responder} disabled={enviando || !novaMsg.trim()}>
                  Enviar
                </Button>
              </Box>
            </Stack>
          </Card>
        </Stack>
      )}
    </DashboardContent>
  );
}
